import { Outlet } from '@tanstack/react-router';
import { Menu, Search } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useZendeskWidget } from '../../shared/hooks';
import { getEnvironmentBanner, getModifierKey } from '../../shared/utils';
import {
  BrokerContextSelector,
  GlobalCommandBar,
  NotificationBell,
  Sidebar,
  UserProfileMenu,
} from './components';

export function AppShellLayout() {
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const modifierKey = getModifierKey();
  const environmentBanner = getEnvironmentBanner();
  // Sidebar e dropdowns sao `fixed` (fora do fluxo) e precisam saber onde
  // o header termina. Com a faixa de ambiente o header desce 28px (h-7).
  const headerBottom = environmentBanner ? '92px' : '64px';

  // Chat de atendimento (Zendesk) em todas as telas logadas.
  useZendeskWidget();

  return (
    <div
      className='relative flex h-dvh max-h-dvh flex-col overflow-hidden overscroll-none bg-alba-white-500'
      style={{ '--app-header-bottom': headerBottom } as CSSProperties}
    >
      {/* Faixa de identificação do ambiente, resolvida pelo host. Produção
          (portal.alba.com.br) não recebe faixa. */}
      {environmentBanner && (
        <div
          className={`flex h-7 w-full shrink-0 items-center justify-center text-xs font-bold uppercase tracking-widest text-white ${environmentBanner.className}`}
        >
          {environmentBanner.label}
        </div>
      )}
      <header className='sticky top-0 z-50 flex h-16 w-full items-center overflow-visible border-b border-alba-green-500 bg-alba-blue-600'>
        <div className='flex shrink-0 items-center gap-1 pl-1 pr-2 sm:gap-3 sm:px-4'>
          <button
            type='button'
            aria-label='Abrir menu lateral'
            className='-ml-0.5 flex h-8 w-8 items-center justify-center rounded-md text-white hover:bg-alba-blue-500 md:hidden'
            onClick={() => setMobileSidebarOpen(true)}
          >
            <Menu className='h-4.5 w-4.5' strokeWidth={1.8} />
          </button>
          <img src='/alba-symbol.svg' alt='Alba Symbol' className='h-10' />
        </div>
        <div className='flex min-w-0 flex-1 items-center px-1 sm:px-4'>
          <button
            onClick={() => setCommandOpen(true)}
            className='flex w-auto min-w-32 items-center gap-2 rounded-md bg-alba-blue-500 px-3 py-2 text-sm text-white transition-colors hover:bg-alba-blue-700 sm:w-full sm:min-w-0 sm:max-w-lg'
          >
            <Search className='h-4.5 w-4.5' strokeWidth={1.8} />
            <span className='min-w-0 truncate text-left text-alba-blue-100 md:hidden'>
              Pesquisar
            </span>
            <span className='hidden md:inline'>Buscar</span>
            <kbd className='ml-auto hidden items-center gap-1 rounded border border-alba-blue-400 bg-alba-blue-600 px-1.5 font-mono text-[10px] font-medium text-white lg:inline-flex'>
              <span className='text-xs'>{modifierKey}</span>
              {modifierKey === 'Ctrl' ? '+' : ''}K
            </kbd>
          </button>
        </div>
        <div className='flex shrink-0 items-center gap-1 px-1 sm:min-w-0 sm:gap-3 sm:px-4'>
          <NotificationBell />
          <BrokerContextSelector />
          <UserProfileMenu />
        </div>
      </header>
      <div className='flex min-h-0 min-w-0 flex-1 overflow-hidden'>
        <Sidebar
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />
        <main className='min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 pb-[max(2rem,env(safe-area-inset-bottom))] lg:p-6 [overflow-anchor:none]'>
          <Outlet />
        </main>
      </div>
      <GlobalCommandBar open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
  );
}
